import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderApi } from '@formosa/shared/api/order';
import { ApiError } from '@formosa/shared/api/request';
import { formatThumbChange, formatThumbPrice, formatThumbVolume, normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';
import { useAuth } from '../../auth/AuthContext';
import { useWallet } from '../../context/WalletContext';
import useProductSearch from '../../context/ProductSearchContext';
import FuturesCandlestickChart from '../../components/trade/FuturesCandlestickChart';
import PerpetualEntrustmentCard from '../../components/trade/PerpetualEntrustmentCard';
import PerpetualOrderModal from '../../components/trade/PerpetualOrderModal';
import PerpetualPositionCard from '../../components/trade/PerpetualPositionCard';
import PerpetualTpSlModal from '../../components/trade/PerpetualTpSlModal';
import useMarketChart from '../../hooks/useMarketChart';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';
import useRequireAuth from '../../hooks/useRequireAuth';
import {
  isPerpetualOpen,
  isPerpetualPending,
  parsePerpetualPlayConfig,
  pickPerpetualProductBySymbol,
} from '../../utils/perpetualPlayConfig';
import { formatPrice } from '../../utils/timedPlayConfig';

export default function PerpetualTradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get('symbol');
  const { isAuthenticated } = useAuth();
  const { balance, refreshWallet } = useWallet();
  const requireAuth = useRequireAuth();
  const { openSearch } = useProductSearch();

  const [products, setProducts] = useState([]);
  const [primaryProduct, setPrimaryProduct] = useState(null);
  const [positions, setPositions] = useState([]);
  const [entrustments, setEntrustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [positionTab, setPositionTab] = useState('OPEN');
  const [hideOtherContracts, setHideOtherContracts] = useState(false);

  const [orderDirection, setOrderDirection] = useState('LONG');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderAmount, setOrderAmount] = useState('');
  const [leverage, setLeverage] = useState('');
  const [openType, setOpenType] = useState('MARKET');
  const [limitPrice, setLimitPrice] = useState('');
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [stopLossPrice, setStopLossPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [tpSlTarget, setTpSlTarget] = useState(null);
  const [tpSlSubmitting, setTpSlSubmitting] = useState(false);
  const [closingOrderId, setClosingOrderId] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  const symbolCode = primaryProduct?.marketSymbolCode || '';
  const thumbMap = useMarketThumbSocket(symbolCode ? [symbolCode] : []);
  const thumb = symbolCode ? thumbMap[normalizeSymbolCode(symbolCode)] : null;
  const { bars, loading: chartLoading } = useMarketChart(symbolCode, {
    enabled: Boolean(symbolCode),
  });

  const priceChange = useMemo(() => formatThumbChange(thumb), [thumb]);
  const currentPrice = thumb?.price ?? thumb?.close;

  const filterBySymbol = useCallback(
    (list) => {
      if (!hideOtherContracts || !symbolCode) {
        return list;
      }
      const normalized = String(symbolCode).trim().toUpperCase();
      return list.filter(
        (item) => String(item.marketSymbolCode || '').trim().toUpperCase() === normalized
      );
    },
    [hideOtherContracts, symbolCode]
  );

  const visiblePositions = useMemo(
    () => filterBySymbol(positions),
    [positions, filterBySymbol]
  );
  const visibleEntrustments = useMemo(
    () => filterBySymbol(entrustments),
    [entrustments, filterBySymbol]
  );

  const hasActiveOrders = useMemo(
    () => visiblePositions.length > 0 || visibleEntrustments.length > 0,
    [visiblePositions, visibleEntrustments]
  );

  function resolveOrderProduct(order) {
    if (!order?.productId) {
      return primaryProduct;
    }
    return products.find((item) => item.id === order.productId) || primaryProduct;
  }

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await orderApi.listPerpetualProducts();
      const rows = Array.isArray(list) ? list : [];
      setProducts(rows);
      const primary = pickPerpetualProductBySymbol(rows, symbolParam);
      setPrimaryProduct(primary);
      if (primary) {
        const cfg = parsePerpetualPlayConfig(primary.playConfig);
        setLeverage(String(cfg.defaultLeverage));
      }
    } catch (err) {
      setProducts([]);
      setPrimaryProduct(null);
      setError(err instanceof ApiError ? err.message : '載入商品失敗');
    } finally {
      setLoading(false);
    }
  }, [symbolParam]);

  const loadOrders = useCallback(async () => {
    if (!isAuthenticated) {
      setPositions([]);
      setEntrustments([]);
      return;
    }
    try {
      const [openPage, pendingPage] = await Promise.all([
        orderApi.listPerpetualOrders({ pageNo: 1, pageSize: 20, status: 'OPEN' }),
        orderApi.listPerpetualOrders({ pageNo: 1, pageSize: 20, status: 'PENDING' }),
      ]);
      setPositions((openPage?.records || []).filter(isPerpetualOpen));
      setEntrustments((pendingPage?.records || []).filter(isPerpetualPending));
    } catch {
      setPositions([]);
      setEntrustments([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }
    const intervalMs = hasActiveOrders ? 3000 : 10000;
    const timer = setInterval(loadOrders, intervalMs);
    return () => clearInterval(timer);
  }, [isAuthenticated, hasActiveOrders, loadOrders]);

  function openOrderModal(direction) {
    if (!primaryProduct?.id) {
      return;
    }
    if (!isAuthenticated) {
      const returnPath = symbolParam
        ? `/trade/perpetual?symbol=${encodeURIComponent(symbolParam)}`
        : '/trade/perpetual';
      requireAuth(returnPath);
      return;
    }
    setOrderDirection(direction);
    setOrderAmount('');
    setOpenType('MARKET');
    setLimitPrice('');
    setTakeProfitEnabled(false);
    setTakeProfitPrice('');
    setStopLossPrice('');
    setError('');
    setOrderModalOpen(true);
  }

  async function handleConfirmOrder() {
    if (!primaryProduct?.id || !orderAmount || Number(orderAmount) <= 0) {
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await orderApi.placePerpetualOrder({
        productId: primaryProduct.id,
        orderType: orderDirection,
        betAmount: Number(orderAmount),
        contractMultiple: leverage ? Number(leverage) : undefined,
        isCurrentPriceOrder: openType === 'MARKET',
        orderPrice: openType === 'LIMIT' && limitPrice ? Number(limitPrice) : undefined,
        takeProfitPrice: takeProfitEnabled && takeProfitPrice ? Number(takeProfitPrice) : undefined,
        stopLossPrice: takeProfitEnabled && stopLossPrice ? Number(stopLossPrice) : undefined,
      });
      setOrderModalOpen(false);
      refreshWallet();
      loadOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '下單失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleClosePosition(order) {
    if (!order?.orderSerialId || closingOrderId) {
      return;
    }
    setClosingOrderId(order.orderSerialId);
    setError('');
    try {
      await orderApi.closePerpetualOrder(order.orderSerialId);
      refreshWallet();
      loadOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '平倉失敗');
    } finally {
      setClosingOrderId(null);
    }
  }

  async function handleCancelEntrustment(order) {
    if (!order?.orderSerialId || cancellingOrderId) {
      return;
    }
    setCancellingOrderId(order.orderSerialId);
    setError('');
    try {
      await orderApi.cancelPerpetualOrder(order.orderSerialId);
      refreshWallet();
      loadOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '撤單失敗');
    } finally {
      setCancellingOrderId(null);
    }
  }

  async function handleConfirmTpSl(payload) {
    if (!tpSlTarget?.id || tpSlSubmitting) {
      return;
    }
    setTpSlSubmitting(true);
    setError('');
    try {
      await orderApi.updatePerpetualOrder({
        orderId: tpSlTarget.id,
        takeProfitPrice: payload.takeProfitPrice,
        stopLossPrice: payload.stopLossPrice,
      });
      setTpSlTarget(null);
      loadOrders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新失敗');
    } finally {
      setTpSlSubmitting(false);
    }
  }

  async function handleCloseAll() {
    if (!visiblePositions.length || closingOrderId) {
      return;
    }
    for (const order of visiblePositions) {
      await handleClosePosition(order);
    }
  }

  const displayCode = primaryProduct?.displayName || symbolCode || '—';
  const pairLabel = primaryProduct?.name || `${symbolCode}/USDT`;
  const volumeLabel = formatThumbVolume(thumb?.volume);

  return (
    <div className="timed-trade-page perpetual-trade-page">
      <header className="timed-trade-page__header">
        <button type="button" className="timed-trade-page__back" aria-label="返回" onClick={() => navigate(-1)}>
          ‹
        </button>
        <h1>{pairLabel}</h1>
        <button type="button" className="timed-trade-page__search" aria-label="搜索" onClick={openSearch}>
          ⌕
        </button>
      </header>

      {error && <div className="timed-trade-page__error">{error}</div>}
      {loading && <p className="timed-trade-page__status">載入中…</p>}

      {!loading && primaryProduct && (
        <>
          <section className="timed-trade-page__quote">
            <div className="timed-trade-page__symbol">{displayCode}</div>
            <div className="timed-trade-page__price">{formatThumbPrice(thumb?.price, 4)}</div>
            <div className={`timed-trade-page__change ${priceChange.positive ? 'is-up' : 'is-down'}`}>
              {priceChange.text}
            </div>
            <div className="timed-trade-page__stats">
              <div><span>最高</span><strong>{formatPrice(thumb?.high, 2)}</strong></div>
              <div><span>最低</span><strong>{formatPrice(thumb?.low, 2)}</strong></div>
              <div><span>成交量</span><strong>{volumeLabel}</strong></div>
            </div>
          </section>

          <section className="timed-trade-page__chart" aria-label="K 線圖">
            <FuturesCandlestickChart
              bars={bars}
              currentPrice={currentPrice}
              loading={chartLoading}
              height={200}
            />
          </section>

          <section className="timed-trade-page__positions">
            <div className="perpetual-trade-page__tabs">
              <button
                type="button"
                className={positionTab === 'OPEN' ? 'is-active' : ''}
                onClick={() => setPositionTab('OPEN')}
              >
                當前持倉
              </button>
              <button
                type="button"
                className={positionTab === 'PENDING' ? 'is-active' : ''}
                onClick={() => setPositionTab('PENDING')}
              >
                當前委託
              </button>
            </div>

            <div className="perpetual-trade-page__toolbar">
              <label className="perpetual-trade-page__hide">
                <input
                  type="checkbox"
                  checked={hideOtherContracts}
                  onChange={(e) => setHideOtherContracts(e.target.checked)}
                />
                <span>隱藏其他合約</span>
              </label>
              {positionTab === 'OPEN' && visiblePositions.length > 0 && (
                <button type="button" className="perpetual-trade-page__close-all" onClick={handleCloseAll}>
                  全部平倉
                </button>
              )}
            </div>

            {positionTab === 'OPEN' ? (
              visiblePositions.length === 0 ? (
                <div className="timed-trade-page__empty">
                  <div aria-hidden>📄</div>
                  <p>無資料</p>
                </div>
              ) : (
                <ul className="futures-trade-page__position-list">
                  {visiblePositions.map((item) => (
                    <PerpetualPositionCard
                      key={item.id || item.orderSerialId}
                      order={item}
                      product={resolveOrderProduct(item)}
                      currentPrice={currentPrice}
                      balance={balance}
                      closing={closingOrderId === item.orderSerialId}
                      onClosePosition={handleClosePosition}
                      onEditTpSl={setTpSlTarget}
                    />
                  ))}
                </ul>
              )
            ) : visibleEntrustments.length === 0 ? (
              <div className="timed-trade-page__empty">
                <div aria-hidden>📄</div>
                <p>無資料</p>
              </div>
            ) : (
              <ul className="futures-trade-page__position-list">
                {visibleEntrustments.map((item) => (
                  <PerpetualEntrustmentCard
                    key={item.id || item.orderSerialId}
                    order={item}
                    product={resolveOrderProduct(item)}
                    currentPrice={currentPrice}
                    cancelling={cancellingOrderId === item.orderSerialId}
                    onCancel={handleCancelEntrustment}
                    onEditTpSl={setTpSlTarget}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <div className="timed-trade-page__actions">
        <button type="button" className="timed-trade-page__buy is-up" onClick={() => openOrderModal('LONG')}>
          買漲
        </button>
        <button type="button" className="timed-trade-page__buy is-down" onClick={() => openOrderModal('SHORT')}>
          買跌
        </button>
      </div>

      <PerpetualOrderModal
        open={orderModalOpen}
        direction={orderDirection}
        product={primaryProduct}
        currentPrice={currentPrice}
        priceChange={priceChange}
        thumb={thumb}
        leverage={leverage}
        amount={orderAmount}
        openType={openType}
        limitPrice={limitPrice}
        takeProfitEnabled={takeProfitEnabled}
        takeProfitPrice={takeProfitPrice}
        stopLossPrice={stopLossPrice}
        submitting={submitting}
        onClose={() => setOrderModalOpen(false)}
        onLeverageChange={setLeverage}
        onAmountChange={setOrderAmount}
        onOpenTypeChange={setOpenType}
        onLimitPriceChange={setLimitPrice}
        onTakeProfitEnabledChange={setTakeProfitEnabled}
        onTakeProfitPriceChange={setTakeProfitPrice}
        onStopLossPriceChange={setStopLossPrice}
        onConfirm={handleConfirmOrder}
      />

      <PerpetualTpSlModal
        open={Boolean(tpSlTarget)}
        order={tpSlTarget}
        currentPrice={currentPrice}
        submitting={tpSlSubmitting}
        onClose={() => setTpSlTarget(null)}
        onConfirm={handleConfirmTpSl}
      />
    </div>
  );
}
